import { useEventListener } from "@vueuse/core";
import type { UserDetails } from "~vue/appTypes";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { inject, onBeforeUnmount, onMounted, type Ref, shallowRef } from "vue";

const linkViewsLogged = new Set<string>();

type NadiaTutorialEntrySource = "sidebar" | "mobile_more_sheet";
type LogTutorialEvent = (eventName: string, extraData: Record<string, unknown>) => void;
type ScrollDepth = 25 | 50 | 75 | 100;

export function useNadiaTutorialAnalytics() {
    const userDetails = inject<Ref<UserDetails>>("user_details");

    const logTutorialEvent: LogTutorialEvent = (eventName, extraData) => {
        void logUserInteraction(eventName, {
            ...extraData,
            ...(userDetails?.value?.nadia_tutorial_analytics_context ?? {}),
        });
    };

    const logLinkViewed = (entrySource: NadiaTutorialEntrySource, navOpen: boolean) => {
        if (linkViewsLogged.has(entrySource)) {
            return;
        }

        linkViewsLogged.add(entrySource);
        logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_LINK_VIEWED, {
            entry_source: entrySource,
            nav_open: navOpen,
        });
    };

    return { logTutorialEvent, logLinkViewed };
}

/**
 * Scroll-depth engagement for the Nadia Tutorial page only. Emits `nadia_tutorial_section_viewed`
 * so depth can be sliced by cohort bucket in Amplitude funnels, which FullStory heatmaps cannot do.
 * Remove this composable (and the depth markers in NadiaTutorial.vue) if the page is removed, or if
 * Amplitude scroll-depth autocapture is adopted for the app.
 */
export function useNadiaTutorialScrollDepth(logTutorialEvent: LogTutorialEvent) {
    const markerRefs = shallowRef<Element[]>([]);
    const depthsLogged = new Set<ScrollDepth>();
    let observer: IntersectionObserver | null = null;

    onMounted(() => {
        observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) {
                    continue;
                }

                const depth = Number((entry.target as HTMLElement).dataset.scrollDepth) as ScrollDepth;
                if (depthsLogged.has(depth)) {
                    continue;
                }

                depthsLogged.add(depth);
                logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_SECTION_VIEWED, { depth });
                observer?.unobserve(entry.target);
            }
        });

        for (const marker of markerRefs.value) {
            observer.observe(marker);
        }
    });

    onBeforeUnmount(() => {
        observer?.disconnect();
    });

    return { markerRefs };
}

/**
 * Dwell-time engagement for the Nadia Tutorial page only. Emits `nadia_tutorial_page_closed` with
 * `active_ms` so tutorial visitors can be compared to non-visitors in Amplitude by time spent.
 * Remove this composable if the page is removed, or if Amplitude session/dwell autocapture is adopted.
 */
export function useNadiaTutorialPageClosed(logTutorialEvent: LogTutorialEvent) {
    let segmentStartedAt = globalThis.document.visibilityState === "visible" ? globalThis.performance.now() : null;
    let accumulatedMs = 0;
    let closed = false;

    const close = () => {
        if (closed) {
            return;
        }

        closed = true;
        if (segmentStartedAt !== null) {
            accumulatedMs += globalThis.performance.now() - segmentStartedAt;
            segmentStartedAt = null;
        }

        if (accumulatedMs > 0) {
            logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_PAGE_CLOSED, { active_ms: Math.round(accumulatedMs) });
        }
    };

    useEventListener(globalThis.document, "visibilitychange", () => {
        if (globalThis.document.visibilityState === "hidden") {
            if (segmentStartedAt !== null) {
                accumulatedMs += globalThis.performance.now() - segmentStartedAt;
            }
            segmentStartedAt = null;
        } else if (!closed) {
            segmentStartedAt = globalThis.performance.now();
        }
    });
    useEventListener(globalThis, "pagehide", close);

    onBeforeUnmount(close);
}
